import { LoadStatus, PortalStore, portalStoreToken } from '@servicetitan/cp-ui';
import { InputFieldState } from '@servicetitan/form-state';
import { inject, injectable } from '@servicetitan/react-ioc';
import { AuthApi, Password, NewPasswordModel } from 'api';
import { FormState } from 'formstate';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { createPasswordValidator } from '../utils/password.validator';

@injectable()
export class AuthRecoveryStore {
    @observable submitStatus = LoadStatus.None;
    @observable loading = LoadStatus.None;
    @observable showPasswordUpdated = false;

    @computed get canSubmit() {
        return !!this.form.$.password.value && !this.form.hasError;
    }

    passwordValidator = createPasswordValidator();

    form = new FormState({
        password: new InputFieldState(''),
        confirm: new InputFieldState(''),
    });

    private code?: string;

    constructor(
        @inject(portalStoreToken) private portalStore: PortalStore,
        @inject(AuthApi) private authApi: AuthApi
    ) {
        makeObservable(this);

        this.form.$.confirm.validators(
            value => !value.length && 'Enter a password',
            val => val !== this.form.$.password.value && "Passwords don't match"
        );

        this.form.$.password.validators(
            value => !value.length && 'Enter a password',
            this.passwordValidator.formValidator
        );
    }

    @action
    init = async (code?: string) => {
        this.showPasswordUpdated = false;
        this.code = code;

        if (!code) {
            this.loading = LoadStatus.Error;
            return;
        }

        this.loading = LoadStatus.Loading;

        try {
            await this.authApi.client.validatePasswordResetToken(code);

            runInAction(() => (this.loading = LoadStatus.Ok));
        } catch {
            runInAction(() => (this.loading = LoadStatus.Error));
        }
    };

    submit = async () => {
        const isValid = await this.form.validate();

        if (!isValid || !this.code) {
            return;
        }

        runInAction(() => (this.submitStatus = LoadStatus.Loading));

        try {
            await this.authApi.client.updatePassword(
                this.code,
                new NewPasswordModel({
                    newPassword: new Password({ value: this.form.$.password.value }),
                })
            );

            runInAction(() => {
                this.showPasswordUpdated = true;
                this.submitStatus = LoadStatus.Ok;
            });
        } catch {
            this.showErrorMessage();
            runInAction(() => (this.submitStatus = LoadStatus.Error));
        }
    };

    showErrorMessage = () => {
        this.portalStore.event('notify', {
            title: 'An error occurred',
            description: 'Please, try again later.',
            status: 'critical',
        });
    };
}
